import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, MapPin, Video } from 'lucide-react';
import { format, isPast } from 'date-fns';
import { nl } from 'date-fns/locale';
import { trajectColumn, type TrajectKind } from '@/lib/traject';

type Doel = Exclude<TrajectKind, 'geen'>;

interface EntityAppointmentsProps {
  scope: Doel;
  entityId: string;
  /** Hoeveel afspraken tonen. Standaard 10. */
  limit?: number;
}

type Afspraak = {
  id: string;
  subject: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  teams_join_url: string | null;
  organizer_email: string | null;
  attendees: unknown;
};

const LEEG_TEKST: Record<Doel, string> = {
  project: 'Nog geen afspraak aan dit project gekoppeld.',
  kavel: 'Nog geen afspraak aan deze kavel gekoppeld.',
  opportunity: 'Nog geen afspraak aan deze opportunity gekoppeld.',
};

/** Hoeveel genodigden, zonder de jsonb-vorm te vertrouwen. */
function aantalGenodigden(attendees: unknown): number {
  return Array.isArray(attendees) ? attendees.length : 0;
}

/**
 * Afspraken van één traject (T2.3).
 *
 * Dit is waar het ticket eigenlijk om ging: op een projectpagina zien wanneer je
 * er geweest bent en met wie. De trajectkolommen uit migratie 033 zijn daar
 * alleen de voorwaarde voor.
 *
 * **Je ziet hier alleen je eigen afspraken.** De RLS op
 * `microsoft_calendar_events` is per gebruiker, en dat blijft zo — een gedeelde
 * teamagenda is een ander besluit dan een koppeling aan een traject. Zolang maar
 * één iemand zijn agenda gekoppeld heeft, valt dat verschil niet op; met twee is
 * het zichtbaar en hoort het uitgelegd te worden in plaats van stilzwijgend.
 */
export function EntityAppointments({ scope, entityId, limit = 10 }: EntityAppointmentsProps) {
  const kolom = trajectColumn(scope);

  const { data: afspraken = [], isLoading, isError } = useQuery({
    queryKey: ['entity-appointments', scope, entityId, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('microsoft_calendar_events')
        .select('id, subject, start_time, end_time, location, teams_join_url, organizer_email, attendees')
        .eq(kolom as any, entityId)
        .order('start_time', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as unknown as Afspraak[];
    },
    enabled: Boolean(entityId),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarDays className="h-5 w-5" />
          Afspraken ({afspraken.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="py-4 text-center text-muted-foreground">Laden...</p>
        ) : isError ? (
          <p className="py-4 text-center text-destructive">Fout bij laden van afspraken</p>
        ) : afspraken.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{LEEG_TEKST[scope]}</p>
        ) : (
          <div className="space-y-2">
            {afspraken.map((a) => {
              const start = a.start_time ? new Date(a.start_time) : null;
              const geweest = start ? isPast(start) : false;
              const genodigden = aantalGenodigden(a.attendees);

              return (
                <div
                  key={a.id}
                  className="flex items-start justify-between gap-3 rounded-lg border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex flex-wrap items-center gap-2">
                      <h4 className="truncate text-sm font-medium">{a.subject || '(geen onderwerp)'}</h4>
                      <Badge variant={geweest ? 'secondary' : 'default'} className="text-xs">
                        {geweest ? 'Geweest' : 'Komt nog'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {start ? format(start, 'EEEE d MMMM yyyy, HH:mm', { locale: nl }) : 'Geen datum'}
                      {genodigden > 0 && ` · ${genodigden} genodigde${genodigden === 1 ? '' : 'n'}`}
                    </p>
                    {a.location && (
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{a.location}</span>
                      </p>
                    )}
                  </div>
                  {a.teams_join_url && !geweest && (
                    <a
                      href={a.teams_join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                      title="Deelnemen aan Teams-vergadering"
                    >
                      <Video className="h-4 w-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
